/**
 * User-defined funnel steps for the Signals → Funnels panel.
 *
 * The analytics-svc funnel endpoint accepts an ordered `?steps=` mini-DSL (see
 * services/analytics-svc/internal/api/funnels.go `parseFunnelSteps`). This module
 * is the pure, dependency-free bridge: it models a step, serializes a step list
 * to that DSL, and persists the definition per property in localStorage so a
 * marketer's funnel survives page reloads. It never throws.
 *
 * Scope: local to the browser (same convention as chatHistory + the Dashboard
 * widgets). A shared/team-wide funnel definition would need a backend store.
 */

export type FunnelStepKind = "pageview" | "click" | "event" | "scroll";

export interface FunnelStepDef {
  kind: FunnelStepKind;
  /** For "event": the event_name (blank = any event). For "scroll": depth 0..1. */
  value?: string;
}

/** Mirrors analytics-svc `maxFunnelSteps`. */
export const MAX_FUNNEL_STEPS = 8;
/** A funnel needs at least an entry step + one more to be meaningful. */
export const MIN_FUNNEL_STEPS = 2;

/** The classic view → interact → convert funnel (matches the backend default). */
export const DEFAULT_FUNNEL_STEPS: readonly FunnelStepDef[] = [
  { kind: "pageview" },
  { kind: "click" },
  { kind: "event" },
];

const KINDS: FunnelStepKind[] = ["pageview", "click", "event", "scroll"];

/** A fresh copy of the default steps (never hand out the frozen constant). */
export function defaultFunnelSteps(): FunnelStepDef[] {
  return DEFAULT_FUNNEL_STEPS.map((s) => ({ ...s }));
}

/**
 * Clamp a scroll depth to (0,1]; default 0.5. Accepts "0.5" (fraction) or "50"
 * (percent). Returns a compact numeric string for the DSL.
 */
export function normalizeDepth(value: string | undefined): string {
  const raw = (value ?? "").trim();
  let n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return "0.5";
  if (n > 1) n = n / 100; // a value > 1 is a percent (e.g. 50 → 0.5)
  if (n > 1) n = 1;
  return String(Math.round(n * 100) / 100);
}

/** Serialize steps to the analytics-svc `?steps=` DSL. */
export function serializeFunnelSteps(steps: FunnelStepDef[]): string {
  return steps
    .map((s) => {
      switch (s.kind) {
        case "pageview":
          return "pageview";
        case "click":
          return "click";
        case "event":
          return s.value?.trim() ? `event:${s.value.trim()}` : "event";
        case "scroll":
          return `scroll:${normalizeDepth(s.value)}`;
      }
    })
    .join(",");
}

function isStep(v: unknown): v is FunnelStepDef {
  if (!v || typeof v !== "object") return false;
  const s = v as Record<string, unknown>;
  return (
    KINDS.includes(s.kind as FunnelStepKind) &&
    (s.value === undefined || typeof s.value === "string")
  );
}

const PREFIX = "signals.funnel.";

function storage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Load a property's saved funnel definition, or the default when there is none /
 * it's unparseable. Caps to MAX_FUNNEL_STEPS. Never throws.
 */
export function loadFunnelSteps(siteId: string | undefined | null): FunnelStepDef[] {
  const ls = storage();
  if (!siteId || !ls) return defaultFunnelSteps();
  try {
    const raw = ls.getItem(PREFIX + siteId);
    if (!raw) return defaultFunnelSteps();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaultFunnelSteps();
    const valid = parsed.filter(isStep).slice(0, MAX_FUNNEL_STEPS);
    return valid.length ? valid : defaultFunnelSteps();
  } catch {
    return defaultFunnelSteps();
  }
}

/**
 * Persist a property's funnel definition (capped). Saving the default removes the
 * key so the property falls back to the backend default cleanly. Never throws.
 */
export function saveFunnelSteps(
  siteId: string | undefined | null,
  steps: FunnelStepDef[],
): void {
  const ls = storage();
  if (!siteId || !ls) return;
  try {
    const capped = steps.slice(0, MAX_FUNNEL_STEPS);
    if (serializeFunnelSteps(capped) === serializeFunnelSteps(defaultFunnelSteps())) {
      ls.removeItem(PREFIX + siteId);
      return;
    }
    ls.setItem(PREFIX + siteId, JSON.stringify(capped));
  } catch {
    /* quota / disabled storage — degrade silently */
  }
}
